import React, { useState, useRef, useEffect, DragEvent, ChangeEvent } from 'react';
import { Form, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Originador } from './model/originador';

interface ImageUploaderProps {
  onImageChange: (file: File | null) => void;
  initialImageUrl?: string;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  onImageChange, 
  initialImageUrl
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialImageUrl || null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (imageFile) {
      const objectUrl = URL.createObjectURL(imageFile);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } 
    else if (!initialImageUrl) {
        setPreviewUrl(null);
    } 
    else {
        setPreviewUrl(initialImageUrl);
    }
  }, [imageFile, initialImageUrl]);
  const handleFile = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      onImageChange(file);
    } else {
      console.error("Arquivo inválido. Por favor, selecione uma imagem/formato válido.");
    }
  };
  
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleAreaClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  const handleRemoveImage = () => {
    setImageFile(null);
    onImageChange(null);
    if(fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  const dropZoneStyle: React.CSSProperties = {
    border: `2px dashed ${isDragging ? '#007bff' : '#ced4da'}`,
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.3s',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: isDragging ? '#f8f9fa' : '#ffffff',
  };

  const previewImageStyle: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '180px',
    objectFit: 'contain',
  };

  const removeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    zIndex: 1,
  };

  return (
    <Form.Group as={Col} md="6" className="mb-3">
      <Form.Label>Logo da {Originador.DESCRITIVO_GERAL}</Form.Label>
      <div 
        style={dropZoneStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleAreaClick}
      >
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileSelect}
          accept="image/png, image/jpeg, image/gif"
          style={{ display: 'none' }} 
        />
        
        {previewUrl ? (
          <>
            <Button 
              variant="danger" 
              size="sm" 
              style={removeButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
            <img src={previewUrl} alt="Pré-visualização" style={previewImageStyle} />
          </>
        ) : (
          <div>
            <FontAwesomeIcon icon={faImage} size="3x" color="#ced4da" />
            <p className="mt-2 mb-0">Arraste e solte uma imagem aqui,</p>
            <p>ou clique para selecionar</p>
          </div>
        )}
      </div>
    </Form.Group>
  );
};

export default ImageUploader;
